import { useTranslations } from "@zapier/i18n";
import { ChangeEvent, useState } from "react";
import {
  Button,
  Field,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Text,
  TextInput,
  ToggleSwitch,
} from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { Connection } from "@zapier/client";

import { ConvertToManagedImpactList } from "./ConvertToManagedImpactList";
import { ConvertToManagedPreviewCard } from "./ConvertToManagedPreviewCard";
import {
  ConvertToManagedValues,
  useConvertToManagedSubmit,
} from "./useConvertToManagedSubmit";
import styles from "./ConnectionsConvertToManagedModal.module.css";

const CONNECTION_NAME_MAX_LENGTH = 255;
const NAME_INPUT_ID = "convert-to-managed-name";
const SHARE_BACK_ID = "convert-to-managed-share-back";

interface Props {
  id?: number;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  connection?: Connection;
}

interface ContentProps {
  connection: Connection;
  isLoading: boolean;
  onSubmit: (values: ConvertToManagedValues) => void;
  setIsOpen: (isOpen: boolean) => void;
}

function ConvertToManagedContent({
  connection,
  isLoading,
  onSubmit,
  setIsOpen,
}: ContentProps) {
  const t = useTranslations("connections-convert-to-managed.modal");
  const initialTitle = connection.title ?? "";
  const [name, setName] = useState(initialTitle);
  const [shareBack, setShareBack] = useState(true);

  const trimmedName = name.trim();
  const isValid =
    trimmedName.length > 0 && trimmedName.length <= CONNECTION_NAME_MAX_LENGTH;

  const submit = () => {
    if (!isValid || isLoading) {
      return;
    }
    onSubmit({ name: trimmedName, shareBackToPreviousOwner: shareBack });
  };

  return (
    <ModalDialogRoot isOpen={true} onOpenChange={setIsOpen}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          submit();
        }}
      >
        <ModalDialogHeader textAlign="start">
          <span className={styles.header}>
            <Text tag="span" type="ParagraphHeader3">
              {t("title")}
            </Text>
            <Text tag="span" type="MinimalPrint1" color="TextWeakest">
              {initialTitle}
            </Text>
          </span>
        </ModalDialogHeader>

        <ModalDialogBody textAlign="start">
          <div className={styles.body}>
            <div className={styles.preview}>
              <ConvertToManagedPreviewCard connection={connection} />
              <Icon
                aria-hidden
                className={styles.arrow}
                name="arrowBigRight"
                size={24}
              />
              <ConvertToManagedPreviewCard
                connection={connection}
                isManaged
                title={trimmedName || initialTitle}
              />
            </div>

            <Field
              label={t("labels.name")}
              inputId={NAME_INPUT_ID}
              renderInput={(inputProps) => (
                <TextInput
                  {...inputProps}
                  id={NAME_INPUT_ID}
                  isErrored={!isValid}
                  defaultValue={initialTitle}
                  isFullWidth={true}
                  maxLength={CONNECTION_NAME_MAX_LENGTH}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setName(event.target?.value || "")
                  }
                  placeholder={t("placeholders.name")}
                />
              )}
            />

            <div className={styles.shareBack}>
              <ToggleSwitch
                id={SHARE_BACK_ID}
                aria-label={t("labels.share-back")}
                checked={shareBack}
                onChange={(event) => setShareBack(event.currentTarget.checked)}
              />
              <label htmlFor={SHARE_BACK_ID}>
                <Text tag="span" type="Body1">
                  {t("labels.share-back")}
                </Text>
              </label>
            </div>

            <ConvertToManagedImpactList
              connection={connection}
              shareBack={shareBack}
            />
          </div>
        </ModalDialogBody>

        <ModalDialogFooter>
          <Button
            type="button"
            variant="tertiary"
            onPress={() => setIsOpen(false)}
          >
            {t("buttons.cancel")}
          </Button>
          <Button type="submit" isDisabled={!isValid} isPending={isLoading}>
            {t("buttons.convert")}
          </Button>
        </ModalDialogFooter>
      </form>
    </ModalDialogRoot>
  );
}

export default function ConnectionsConvertToManagedModal({
  id,
  isOpen,
  setIsOpen,
  connection,
}: Props) {
  const { submit, isLoading } = useConvertToManagedSubmit({
    id,
    connection,
    isOpen,
    onConverted: () => setIsOpen(false),
  });

  if (!isOpen || !connection) {
    return null;
  }

  return (
    <ConvertToManagedContent
      connection={connection}
      isLoading={isLoading}
      onSubmit={submit}
      setIsOpen={setIsOpen}
    />
  );
}
